import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  c5n4Api,
  WriteoffCandidateItem,
  PendingResponse,
  WriteoffResponse,
  WriteoffFilter,
  FC120Detail,
} from '../../../services/c5n4Api';

export interface UseC5N4Return {
  // State
  loading: boolean;
  writeoffing: boolean;
  candidateItems: WriteoffCandidateItem[];
  total: number;
  selectedKeys: string[];
  filter: WriteoffFilter;
  detailData: FC120Detail | null;
  detailLoading: boolean;
  detailVisible: boolean;

  // Actions
  setFilter: (f: WriteoffFilter) => void;
  setSelectedKeys: (keys: string[]) => void;
  fetchCandidates: () => Promise<void>;
  batchWriteoff: () => Promise<WriteoffResponse | null>;
  loadDetail: (doNo: string) => Promise<void>;
  closeDetail: () => void;
}

export function useC5N4(): UseC5N4Return {
  const [loading, setLoading] = useState(false);
  const [writeoffing, setWriteoffing] = useState(false);
  const [candidateItems, setCandidateItems] = useState<WriteoffCandidateItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [filter, setFilter] = useState<WriteoffFilter>({ doStart: '', doEnd: '' });
  const [detailData, setDetailData] = useState<FC120Detail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setSelectedKeys([]);
    try {
      const resp: PendingResponse = await c5n4Api.getCandidates(filter);
      setCandidateItems(resp.items || []);
      setTotal(resp.total || 0);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadDetail = useCallback(async (doNo: string) => {
    setDetailLoading(true);
    setDetailVisible(true);
    try {
      const data = await c5n4Api.getDetail(doNo);
      setDetailData(data);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '取得明細失敗');
      message.error(msg);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setDetailVisible(false);
    setDetailData(null);
  }, []);

  const batchWriteoff = useCallback(async (): Promise<WriteoffResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆異常交運單進行沖銷');
      return null;
    }
    setWriteoffing(true);
    try {
      const resp = await c5n4Api.writeoff(selectedKeys);
      if (resp.failed > 0) {
        message.warning(`沖銷完成：成功 ${resp.success} 筆，失敗 ${resp.failed} 筆`);
      } else {
        message.success(resp.message || `沖銷完成！共 ${resp.success} 筆`);
      }
      await fetchCandidates();
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
      message.error(msg);
      return null;
    } finally {
      setWriteoffing(false);
    }
  }, [selectedKeys, fetchCandidates]);

  return {
    loading,
    writeoffing,
    candidateItems,
    total,
    selectedKeys,
    filter,
    detailData,
    detailLoading,
    detailVisible,
    setFilter,
    setSelectedKeys,
    fetchCandidates,
    batchWriteoff,
    loadDetail,
    closeDetail,
  };
}
